import useAuth from '@hooks/useAuth';
import { useApi } from '@hooks/useApi';
import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import ChangelogEntry from './ChangelogEntry';
import { ChangelogPage } from './types';

const ChangelogPopup = () => {
    const { t } = useTranslation('common');
    const { user } = useAuth();
    const api = useApi();
    const { addModal, removeModal } = useModalManager();
    const { data, mutate } = useSWR<ChangelogPage>(user?.hasClub ? 'changelogs/unread' : null, {
        revalidateOnFocus: false
    });

    useEffect(() => {
        const changelogs = data?.data ?? [];
        if (!changelogs.length) return;

        const alreadyOpen = useModalManager
            .getState()
            .modals.some((m) => (m as { changelogPopup?: boolean }).changelogPopup);
        if (alreadyOpen) return;

        const markRead = () => {
            mutate({ data: [] }, { revalidate: false });
            api({
                url: 'changelogs/read',
                method: 'POST',
                data: { ids: changelogs.map((c) => c.id) },
                muted: true
            });
        };

        addModal({
            changelogPopup: true,
            title: t('changelog.title'),
            size: 45,
            onClose: markRead,
            content: (modalId: string) => (
                <div className="tw-flex tw-flex-col tw-gap-8">
                    {changelogs.map((changelog) => (
                        <ChangelogEntry key={changelog.id} changelog={changelog} />
                    ))}
                    <div className="tw-sticky tw-bottom-0 -tw-mb-4 tw-flex tw-justify-end tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-bg-white tw-py-3">
                        <Button
                            color="secondary"
                            onPress={() => {
                                markRead();
                                removeModal(modalId);
                            }}
                        >
                            {t('changelog.confirm')}
                        </Button>
                    </div>
                </div>
            )
        } as Parameters<typeof addModal>[0]);
    }, [data]);

    return null;
};

export default ChangelogPopup;
